import { ErrorApi } from "@core/modeles/model.ts";
import {
  ComplaintPostDTO,
  complaintPutDTO,
} from "@modules/plaintes/plaintes.model.ts";
import { ComplaintService } from "@modules/plaintes/plaintes.service.ts";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

const complainteService = new ComplaintService();

interface FilterBase {
  page: number;
  size: number;
  sortBy?: string;
  sortDir?: string;
  search?: string;
  status?: string;
  type?: number | string;
  startDate?: string;
  endDate?: string;
  receivedComplaint?: boolean;
}

interface GUFilter {
  householdId?: string;
  individualId?: string;
}

export const getComplaints = createAsyncThunk(
  "complaint/list",
  async (filter: FilterBase) => {
    const complaintList = await complainteService.getAllComplaintWithPagination(
      filter
    );
    return complaintList;
  }
);

export const getComplaintDetails = createAsyncThunk(
  "complaint/details",
  async (id: number) => {
    const complaintDetails = await complainteService.getComplaintDetails(id);
    return complaintDetails;
  }
);

export const archivedComplaint = createAsyncThunk(
  "complaint/archived",
  async ({
    filter,
    onSuccess,
    onError,
  }: {
    filter: { id: number };
    onSuccess?: () => void;
    onError: (error: ErrorApi) => void;
  }) => {
    try {
      const { id, ...archivedComplaint } = filter;
      const archivedSave = await complainteService.archiveComplaint(
        id,
        archivedComplaint
      );
      if (onSuccess) onSuccess();
      return archivedSave;
    } catch (error) {
      if (onError) onError(error as ErrorApi);
    }
  }
);

export const getSupportType = createAsyncThunk(
  "complaint/supportType",
  async () => {
    const support = await complainteService.getSupportType();
    return support;
  }
);

export const getAllNatureComplaint = createAsyncThunk(
  "complaint/nature",
  async () => {
    const complaintList = await complainteService.getAllNatureComplaint();
    return complaintList;
  }
);

export const saveComplaint = createAsyncThunk(
  "complaint/save",
  async ({
    filter,
    onSuccess,
    onError,
  }: {
    filter: ComplaintPostDTO;
    onSuccess: () => void;
    onError: (error: AxiosError) => void;
  }) => {
    try {
      const complaintSave = await complainteService.saveComplaint(filter);
      if (onSuccess) onSuccess();
      return complaintSave;
    } catch (error) {
      return onError(error as AxiosError);
    }
  }
);

export const editComplaint = createAsyncThunk(
  "complaint/edit",
  async ({
    filter,
    onSuccess,
    onError,
  }: {
    filter: complaintPutDTO & { id: string };
    onSuccess?: () => void;
    onError?: () => void;
  }) => {
    try {
      const { id, ...complaintData } = filter;
      const complaintSaved = await complainteService.editComplaint(
        id,
        complaintData
      );
      if (onSuccess) onSuccess();
      return complaintSaved;
    } catch {
      if (onError) onError();
    }
  }
);

export const downloadComplaintReceipt = createAsyncThunk(
  "complaint/downloadReceipt",
  async (id: number, { rejectWithValue }) => {
    try {
      complainteService.downloadComplaintReceipt(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? "Download failed");
    }
  }
);

export const complaintExport = createAsyncThunk(
  "complaint/export",
  async (filter: FilterBase, { rejectWithValue }) => {
    try {
      const response = await complainteService.getComplaintExport(filter);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getGuichetUnique = createAsyncThunk(
  "guichet-unique/complainant",
  async ({
    filter,
    onSuccess,
    onError,
  }: {
    filter: GUFilter;
    onSuccess?: (a: any) => void;
    onError?: (a: any) => void;
  }, 
    { rejectWithValue }) => {
    try {
      const response = await complainteService.getGuichetUnique(
        filter.householdId as string,
        filter.individualId as string
      );
      if (onSuccess) onSuccess(response);
      return response;
    }catch (error) {
      if (onError) onError(error);
      return rejectWithValue(error);
    }
  }
);

export const getMembersByHouseholdId = createAsyncThunk(
  "guichet-unique/members",
  async ({
    householdId,
    onSuccess,
    onError,
  }: {
    householdId: string;
    onSuccess?: (a: any) => void;
    onError?: (a: any) => void;
  }, 
  { rejectWithValue }) => {
    try {
      const response = await complainteService.getMembersByHouseholdId(householdId);
      if (onSuccess) onSuccess(response);
      return response;
    } catch (error) {
      if (onError) onError(error);
      return rejectWithValue(error);
    }
  }
)
